import React from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import { Formik, Field, FieldArray, Form, ErrorMessage } from 'formik';
/* Components */
import Input from '../../../common/form-elements/input/input';
import Checkbox from '../../../common/form-elements/checkbox/checkbox';
import FormErrorMessage from '../../../common/form-elements/form-error-message/form-error-message';
import ErrorMessageFieldArray from '../../../common/form-elements/form-error-message/error-message-field-array';
import IconButton from '../../../common/icon-button/icon-button';
/* Helpers */
import { maxLength, minLength, required, schemaValidate, urlChecker } from '../../../../helpers/validation-helpers';
import { AUTO_REFRESH_OPTIONS, NUMBER_OF_ITEMS_OPTIONS } from '../widget-constants';

const FEED_SOURCES_LIMIT = 5;

const RssFeedForm = ({ onFormSubmit, onCancel, initialData }) => {
    const handleFormCheckboxChange = (field, props) => ({ target: { checked } }) => props.setFieldValue(field, checked);

    const handleFormFieldChange = (field, props) => value => props.setFieldValue(field, value);

    const addNewUrlSource = (arrayHelpers) => () => {
        arrayHelpers.push('');
    };

    const deleteUrlSource = (arrayHelpers, index) => () => {
        arrayHelpers.remove(index);
    };

    const renderFieldArray = props => {
        return arrayHelpers => (
            <>
                {props.values.config.rss.map((_, index) => (
                    <div
                        className="glu-form-row"
                        /* eslint-disable-next-line react/no-array-index-key */
                        key={`${index}`}
                    >
                        <div className="glu-label">Source URL</div>
                        <div className="glu-flex-row">
                            <Field
                                className="glu-flex-grow glu-form-input-pad"
                                as={Input}
                                name={`config.rss.${index}`}
                                placeholder="RSS feed url"
                                onChange={props.handleChange(`config.rss.${index}`)}
                                validate={schemaValidate([required(), urlChecker()])}
                                invalid={
                                    props.errors.config
                                    && props.errors.config.rss
                                    && props.errors.config.rss[index]
                                    && props.touched.config
                                    && props.touched.config.rss
                                    && props.touched.config.rss[index]
                                }
                            />
                            <IconButton
                                className="glu-flex-shrink"
                                icon="delete"
                                text="Delete"
                                onClick={deleteUrlSource(arrayHelpers, index)}
                            />
                        </div>
                        <ErrorMessageFieldArray name={`config.rss.${index}`} />
                    </div>
                ))}
                {
                    arrayHelpers.form.values.config.rss.length < FEED_SOURCES_LIMIT
                    && (
                        <div className="glu-form-row">
                            <IconButton
                                icon="dashboard-add"
                                text="Add more"
                                onClick={addNewUrlSource(arrayHelpers)}
                            />
                        </div>
                    )
                }
            </>
        );
    };

    return (
        <Formik
            initialValues={{
                title: initialData.title,
                config: {
                    ...initialData.config,
                    hideItemImage: initialData.config.hideItemImage || false,
                    autoRefresh: initialData.config.autoRefresh || AUTO_REFRESH_OPTIONS[0],
                    feedNumberOptions: initialData.config.feedNumberOptions || NUMBER_OF_ITEMS_OPTIONS[1],
                    rss: initialData.config.rss && initialData.config.rss.length
                        ? initialData.config.rss
                        : ['']
                }
            }}
            onSubmit={onFormSubmit}
        >
            {(props) => {
                return (
                    <Form>
                        <div className="glu-modal__body">
                            <div className="glu-content__section">
                                <div className="glu-form-row">
                                    <div className="glu-label">Widget Title</div>
                                    <Field
                                        as={Input}
                                        autoFocus
                                        name="title"
                                        className="glu-form-input-pad"
                                        onChange={props.handleChange('title')}
                                        validate={schemaValidate(
                                            [required(), minLength(), maxLength(30)]
                                        )}
                                        invalid={
                                            props.errors.title
                                            && props.touched.title
                                        }
                                    />
                                    <ErrorMessage name="title" component={FormErrorMessage} />
                                </div>
                                <div className="glu-form-row">
                                    <div className="glu-label">Auto Refresh</div>
                                    <Field
                                        as={Select}
                                        name="autoRefresh"
                                        options={AUTO_REFRESH_OPTIONS}
                                        isSearchable={false}
                                        value={props.values.config.autoRefresh}
                                        onChange={
                                            handleFormFieldChange(
                                                'config.autoRefresh',
                                                props
                                            )
                                        }
                                        className="glu-select glu-form-input-pad
                                                             glu-select__number-of-items"
                                        classNamePrefix="react-select"
                                    />
                                </div>
                                <div className="glu-form-row">
                                    <div className="glu-label">Number of items per RSS feed</div>
                                    <Field
                                        as={Select}
                                        name="feedNumberOptions"
                                        options={NUMBER_OF_ITEMS_OPTIONS}
                                        isSearchable={false}
                                        value={props.values.config.feedNumberOptions}
                                        onChange={
                                            handleFormFieldChange(
                                                'config.feedNumberOptions',
                                                props
                                            )
                                        }
                                        className="glu-select glu-form-input-pad
                                                            glu-select__number-of-items"
                                        classNamePrefix="react-select"
                                    />
                                </div>
                                <div className="glu-form-row">
                                    <Checkbox
                                        onChange={
                                            handleFormCheckboxChange(
                                                'config.hideItemImage',
                                                props
                                            )
                                        }
                                        isChecked={props.values.config.hideItemImage}
                                    >
                                        <div className="glu-label">Hide images in RSS feed</div>
                                    </Checkbox>
                                </div>
                                <FieldArray
                                    name="config.rss"
                                    render={renderFieldArray(props)}
                                />
                            </div>
                        </div>
                        <footer className="glu-modal__footer">
                            <div className="glu-flex-grow glu-flex-row">
                                <div className="glu-flex-grow text-right">
                                    <button
                                        onClick={onCancel}
                                        className="glu-btn glu-btn--secondary"
                                        type="button"
                                    >
                                        Cancel
                                    </button>
                                </div>
                                <div className="glu-flex-shrink">
                                    <button
                                        type="submit"
                                        className="glu-btn glu-btn--primary"
                                    >
                                        Save
                                    </button>
                                </div>
                            </div>
                        </footer>
                    </Form>
                );
            }}
        </Formik>
    );
};

RssFeedForm.propTypes = {
    initialData: PropTypes.objectOf(PropTypes.any).isRequired,
    onFormSubmit: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired
};

export default RssFeedForm;
